import {E} from "js-vextensions";
import {CreateAccessor} from "mobx-graphlink";
import {O, RunInAction} from "web-vcore";
import {EngineConfig} from "../../Server/DB/engineConfigs/@EngineConfig.js";
import {GetSession} from "../../Server/DB/sessions.js";
import {Session} from "../../Server/DB/sessions/@Session.js";
import {store} from "../../Store/index.js";
import {SessionProcessorsState} from "../../UI/@Shared/Processors/@ProcessorInstances.js";
import {GetSessionDerivativeData, SessionExternals} from "./timeline/SessionDerivativeData.js";
import {SessionDataProcessor} from "../../UI/@Shared/Processors/SessionDataProcessor.js";
import {ignore} from "mobx-sync";

export class TimelineState {
	@O accessor subpage = "sessions";
	@O accessor daysToShow = 7;
	@O accessor dayShift = 0;
	@O accessor rowHeight = 70;
	@O accessor specialDisplayMode: "sleepSchedule" | "visualization" | null;
	@O @ignore accessor screenshotMode = false;
	@O accessor message = "";
	//@O activities = new ActivitiesState();
	@O accessor sessions = new SessionsState();
}

export enum SessionUITab {
	general = "general",
	details = "details",
	events = "events",
	//dj = "dj",
}

export enum SessionDetailSideTab {
	"data" = "data",
	"view" = "view",
	"video" = "video",
	"seekbar" = "seekbar",
	"testing" = "testing",
}

/*export class ActivitiesState {
}*/
export class SessionsState {
	@O accessor export_includeAll = true;
	@O accessor export_rangeStart: string|n = null;
	@O accessor export_rangeEnd: string|n = null;

	@O @ignore accessor selectedSession: string|n; // don't persist selected-session for now (loading might be unwanted, and takes too long with no way to cancel)
	@O accessor tab = SessionUITab.general;
	@O accessor sideTab = SessionDetailSideTab.data;
	//@O showDetailPanel = true;
	//@O showDetailPanelSettings = true;
	//@O showSeekBar = true;

	// processors
	processors = new SessionProcessorsState();

	// chart legend
	@O accessor disabledSeries = [] as number[];

	// data
	@O accessor showDeildAttempts = true;
	@O accessor showWakings = true;
	@O accessor showDreamingPeriods = true;
	@O accessor showSnoozePeriods = true;
	@O accessor showSolvingPeriods = true;
	@O accessor showVisPromptPeriods = true;
	@O accessor showAlertPeriods = true;
	@O accessor showSignals = true;
	@O accessor showBaseline = true;
	@O accessor eeg_normalize = true;
	@O accessor eeg_smooth = true;
	@O accessor breath_showIntervalThresholds = true;
	@O accessor breath_calcPeaks = true;
	@O accessor breath_calcLongInhales = false;
	//@O breath_calcDesync = true;
	@O @ignore accessor processing_useCurrentConfig = false;

	// long-inhale debug/testing
	// ----------
	// test-window length (in seconds) after the current-time to record instrumentation for
	@O accessor longInhaleDebug_windowLength = 10;
	// time-window the BreathProcessor records per-sample instrumentation data for (auto-set by "testing" tab)
	@O @ignore accessor longInhaleDebug_startTime: number|n = null;
	@O @ignore accessor longInhaleDebug_endTime: number|n = null;
	// timestamp the mouse is currently hovering over on the detail-graph (null when not hovering)
	@O @ignore accessor hoveredTime: number|n = null;

	/*@O @ignore processing_useCurrentConfig_resimData_currentProcessingStartTime: number;
	@O @ignore accessor processing_useCurrentConfig_resimData_currentProcessingProgress: number;
	@O @ignore accessor processing_useCurrentConfig_resimData: SessionDerivativeData_AllowingResim;
	@O @ignore accessor processing_useCurrentConfig_resimData_use = false;*/
	@O accessor processing_cpuLimit = .45;
	@O accessor simulation_cpuLimit = .45;
	@O accessor rendering_cpuLimit = .1;

	// view (main graph)
	@O accessor maxViewRange = 10 * 60; // in seconds
	@O accessor initialViewRange = 60; // in seconds
	@O accessor moveViewWithTime = true;
	@O accessor viewAnchorPosForTime = .75;

	// camera
	@O accessor video_show = true;
	@O accessor video_autoPlay = false;
	@O accessor playbackSpeed = 1;
	@O accessor moveTimeWithVideo = true;
	@O accessor videoLatency = 0;

	// seek bar
	@O accessor seekBar_show = true;

	//@O @ignore currentTime = 0;
	@O accessor showCurrentTime = true;
	@O accessor currentTime = 0; // in ms (unix timestamp)
	@O @ignore accessor currentTime_nonVideoPlayerSets = 0; // convenience field (eg. so video-player can know if time-change was external and thus necessitates remount)
	@O accessor viewRange_startTime = 0;
	@O accessor viewRange_endTime = 0;
	//@ignore viewRange_changePendingEEGChartReflect = false;
}

export const IsSessionUIOpen = CreateAccessor((requiredTab?: SessionUITab)=>{
	return store.main.page == "timeline" && store.main.timeline.subpage == "sessions" && store.main.timeline.sessions.selectedSession != null && (!requiredTab || store.main.timeline.sessions.tab == requiredTab);
});

export function GetDefaultViewRangeForTime(time: number, session: Session, viewRangeOverride?: number, shiftViewRangeToKeepInRange = false) {
	const uiState = store.main.timeline.sessions;
	const oldViewRangeInMS = uiState.viewRange_endTime - uiState.viewRange_startTime;
	//const viewRangeInMS = uiState.initialViewRange * 1000;
	const viewRangeInMS = viewRangeOverride ?? oldViewRangeInMS;

	let startTime = time - (viewRangeInMS * uiState.viewAnchorPosForTime);
	let endTime = (time + (viewRangeInMS * (1 - uiState.viewAnchorPosForTime))).KeepAtLeast(startTime + 1000);
	if (shiftViewRangeToKeepInRange) {
		const keepInRangeShifts = [
			KeepTimeInSessionRange(startTime, session) - startTime,
			KeepTimeInSessionRange(endTime, session).KeepAtLeast(startTime + 1000) - endTime,
		];
		const shiftToApply = keepInRangeShifts.OrderByDescending(shift=>Math.abs(shift))[0];
		startTime += shiftToApply;
		endTime += shiftToApply;
	}
	return {startTime, endTime};
}
//export function SetCurrentTime(time: number, session: EngineSessionInfo, adjustViewRange = true) {
export class SetCurrentTime_Options {
	adjustViewRange = "allow" as "force" | "allow" | "none";
	fromVideoPlayer = false;
	viewRangeOverride?: number;
}
export function SetCurrentTime(time: number, session: Session, opt?: Partial<SetCurrentTime_Options>) {
	opt = E(new SetCurrentTime_Options(), opt);
	const uiState = store.main.timeline.sessions;
	const adjustViewRange = opt.adjustViewRange == "force" || (opt.adjustViewRange == "allow" && uiState.moveViewWithTime);

	time = KeepTimeInSessionRange(time, session);
	RunInAction("SetCurrentTime", ()=>{
		opt = opt!;
		uiState.currentTime = time;
		if (!opt.fromVideoPlayer) {
			uiState.currentTime_nonVideoPlayerSets++;
		}
		if (adjustViewRange) {
			const newViewRange = GetDefaultViewRangeForTime(time, session, opt.viewRangeOverride, opt.viewRangeOverride != null);
			uiState.viewRange_startTime = newViewRange.startTime;
			uiState.viewRange_endTime = newViewRange.endTime;
			//uiState.viewRange_changePendingEEGChartReflect = true;
		}
	});
}
export function KeepTimeInSessionRange(time: number, session: Session) {
	return time.KeepBetween(session.startTime, session.endTime);
}